import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  formatWeekDateLabel,
  toISODateString,
  getReferenceDateForWeekday,
  dateToOffset,
  offsetToDate,
  startOfWeek,
  todayInBrowserTimeZone,
  type Weekday,
} from "@/utils/dateUtils";
import {
  weekStepperBounds,
  weeksBetween,
  type WeekStepperBounds,
} from "@/utils/weekStepperBounds";
import { computeWeekColumns } from "@/utils/weekUtils";

export type { Weekday };

const STEP_DAYS = 7;

export interface UseWeekStepperProps {
  /** Fixed weekday (0 = Sunday, 1 = Monday, …) */
  weekday: Weekday;
  /** Current value (ISO string or Date) */
  value?: string | Date | null;
  /** Callback when date changes - receives ISO date string */
  onChange?: (value: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /**
   * Minimum allowed date (ISO string or Date).
   * Used to constrain dates to not go before a certain point.
   */
  minDate?: string | Date | null;
  /**
   * Maximum allowed date (ISO string or Date).
   * Used to constrain dates to not go beyond a certain point.
   */
  maxDate?: string | Date | null;
}

export interface UseWeekStepperReturn {
  /** Props for the hidden input element */
  inputProps: {
    "data-value": string;
  };
  /** Step to previous date */
  stepPrev: () => void;
  /** Step to next date */
  stepNext: () => void;
  /** Props for the dropdown list */
  listProps: {
    dates: Array<{ offset: number; date: Date; label: string; isoValue: string }>;
    selectedOffset: number;
    onSelect: (offset: number) => void;
  };
  /** Current date value */
  currentDate: Date;
  /** ISO string value */
  isoValue: string;
  /** Container ref */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Whether input is disabled */
  disabled: boolean;
  /**
   * Told by the view when the dropdown opens or closes. Opening re-anchors the
   * selectable window on the value being shown, which is what lets a
   * permanently-mounted stepper (the board's "Scroll to date") keep moving
   * forward across separate interactions.
   */
  setDropdownOpen: (open: boolean) => void;
}

function parseValue(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** The selectable weeks, inclusive of both bounds. */
function buildDropdownDates(
  bounds: WeekStepperBounds,
  referenceDate: Date,
): Array<{ offset: number; date: Date; label: string; isoValue: string }> {
  const numWeeks = Math.max(1, weeksBetween(bounds.floor, bounds.ceiling) + 1);
  return computeWeekColumns(bounds.floor, numWeeks).map((week) => ({
    offset: dateToOffset(week.start, referenceDate, STEP_DAYS),
    date: week.start,
    // Always spell the year out. A list now routinely spans two calendar years,
    // and the shared default ("Apr 27" this year, "04/26/27" otherwise) mixes two
    // formats a few rows apart — and its numeric form isn't even re-typeable,
    // since parseUserDateInput only accepts month names.
    label: formatWeekDateLabel(week.start, "YYYY"),
    isoValue: toISODateString(week.start),
  }));
}

/**
 * Core hook for WeekStepper component.
 * Handles stepping, dropdown state, and date constraints.
 */
export function useWeekStepper({
  weekday,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
}: UseWeekStepperProps): UseWeekStepperReturn {
  const referenceDate = useMemo(
    () => getReferenceDateForWeekday(weekday),
    [weekday]
  );

  const getSnappedValue = useCallback(() => {
    const parsed = parseValue(value);
    return startOfWeek(parsed ?? todayInBrowserTimeZone());
  }, [value]);

  /**
   * The week the selectable window is anchored to. Frozen for one editing
   * session so the range can't creep forward as the user steps through it:
   * pushing a date out beyond a year takes a second, deliberate edit.
   */
  const [anchor, setAnchor] = useState<Date>(getSnappedValue);
  const [dropdownOpen, setDropdownOpenState] = useState(false);
  // The last value this hook emitted, so an incoming `value` can be recognised
  // as our own edit rather than an external one.
  const lastEmittedRef = useRef<string | null>(null);

  const getInitialOffset = useCallback(() => {
    const initialValue = parseValue(value);
    if (!initialValue) return 0;
    const snapped = startOfWeek(initialValue);
    return dateToOffset(snapped, referenceDate, STEP_DAYS);
  }, [value, referenceDate]);

  const [currentOffset, setCurrentOffset] = useState<number>(getInitialOffset);

  const containerRef = useRef<HTMLDivElement>(null);

  const currentDate = useMemo(
    () => offsetToDate(currentOffset, referenceDate, STEP_DAYS),
    [currentOffset, referenceDate]
  );

  const isoValue = useMemo(() => toISODateString(currentDate), [currentDate]);

  const parsedMinDate = useMemo(() => parseValue(minDate), [minDate]);
  const parsedMaxDate = useMemo(() => parseValue(maxDate), [maxDate]);

  const bounds = useMemo(
    () =>
      weekStepperBounds({
        anchor,
        minDate: parsedMinDate,
        maxDate: parsedMaxDate,
        // The local calendar date, not the raw instant: startOfWeek reads UTC
        // fields, which would put Sunday evening in the Americas in next week.
        today: todayInBrowserTimeZone(),
      }),
    [anchor, parsedMinDate, parsedMaxDate]
  );

  const minOffset = useMemo(
    () => dateToOffset(bounds.floor, referenceDate, STEP_DAYS),
    [bounds.floor, referenceDate]
  );
  const maxOffset = useMemo(
    () => dateToOffset(bounds.ceiling, referenceDate, STEP_DAYS),
    [bounds.ceiling, referenceDate]
  );

  const dropdownDates = useMemo(
    () => buildDropdownDates(bounds, referenceDate),
    [bounds, referenceDate]
  );

  /** Opening the dropdown re-anchors the window on what's currently shown. */
  const setDropdownOpen = useCallback(
    (open: boolean) => {
      setDropdownOpenState(open);
      if (open) setAnchor(startOfWeek(currentDate));
    },
    [currentDate]
  );

  // Sync with controlled value
  useEffect(() => {
    const controlledDate = parseValue(value);
    if (controlledDate) {
      const snapped = startOfWeek(controlledDate);
      const newOffset = dateToOffset(snapped, referenceDate, STEP_DAYS);
      if (newOffset !== currentOffset) {
        setCurrentOffset(newOffset);
      }
    }
  }, [value, referenceDate, currentOffset]);

  /**
   * Re-anchor when the value is changed from outside while the dropdown is shut —
   * e.g. editing a project's start date rewrites its end date, which is derived
   * from it. Our own edits are excluded, or stepping with the dropdown closed
   * would push the ceiling along one week at a time.
   */
  useEffect(() => {
    if (dropdownOpen) return;
    const controlledDate = parseValue(value);
    if (!controlledDate) return;
    const snapped = startOfWeek(controlledDate);
    const iso = toISODateString(snapped);
    if (iso === lastEmittedRef.current) return;
    if (iso === toISODateString(anchor)) return;
    setAnchor(snapped);
  }, [value, dropdownOpen, anchor]);

  const commitDate = useCallback(
    (newOffset: number) => {
      const clampedOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
      setCurrentOffset(clampedOffset);
      const newDate = offsetToDate(clampedOffset, referenceDate, STEP_DAYS);
      const newIsoValue = toISODateString(newDate);
      lastEmittedRef.current = newIsoValue;
      onChange?.(newIsoValue);
    },
    [referenceDate, minOffset, maxOffset, onChange]
  );

  const stepPrev = useCallback(() => {
    if (!disabled && currentOffset > minOffset) {
      commitDate(currentOffset - 1);
    }
  }, [disabled, currentOffset, minOffset, commitDate]);

  const stepNext = useCallback(() => {
    if (!disabled && currentOffset < maxOffset) {
      commitDate(currentOffset + 1);
    }
  }, [disabled, currentOffset, maxOffset, commitDate]);

  const onSelectFromList = useCallback(
    (offset: number) => {
      commitDate(offset);
    },
    [commitDate]
  );

  return {
    inputProps: {
      "data-value": isoValue,
    },
    stepPrev,
    stepNext,
    listProps: {
      dates: dropdownDates,
      selectedOffset: currentOffset,
      onSelect: onSelectFromList,
    },
    currentDate,
    isoValue,
    containerRef: containerRef as React.RefObject<HTMLDivElement>,
    disabled,
    setDropdownOpen,
  };
}
